import { get } from "lodash";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "react-query";

import {
  ILicenseCalculateStatusResponse,
  ILicenseRecalculateParams,
} from "~/entity/Licenses/model/schema";
import { useAddNotificationMutation } from "~/schemas/generated/graphql";
import { defaultErrorHandlerFunc } from "~/shared/hooks/useRequestHandler";

import { useLicenseRequests } from "./useLicenseRequests";

const POLL_INTERVAL = 2000;

export const useCalculateLicenseNeed = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [addNotificationMutation] = useAddNotificationMutation({});
  const {
    queryKeys,
    calculateRequest,
    recalculateRequest,
    getCalculateStatus,
  } = useLicenseRequests();
  const [jobId, setJobId] = useState<string>();
  const invalidatedJobRef = useRef<string>();

  const startMutation = useMutation(
    (params?: ILicenseRecalculateParams) =>
      params ? recalculateRequest(params) : calculateRequest({}),
    {
      onSuccess: (response) => {
        const nextJobId = get(response, "data.jobId") as string | undefined;
        setJobId(nextJobId);
      },
      onError: (e) => {
        defaultErrorHandlerFunc({ e, t, addNotificationMutation });
      },
    },
  );

  const statusQuery = useQuery({
    queryKey: ["licenseRequestsCalculateStatus", jobId],
    queryFn: () => getCalculateStatus(String(jobId)),
    enabled: Boolean(jobId),
    refetchInterval: (data) => {
      const status = get(data, "data.status") as
        | ILicenseCalculateStatusResponse["status"]
        | undefined;
      return !status || status === "RUNNING" ? POLL_INTERVAL : false;
    },
  });

  const job = get(statusQuery.data, "data") as
    | ILicenseCalculateStatusResponse
    | undefined;

  useEffect(() => {
    if (job?.status === "DONE" && invalidatedJobRef.current !== job.id) {
      invalidatedJobRef.current = job.id;
      queryClient.invalidateQueries(queryKeys.all);
    }
  }, [job, queryClient, queryKeys.all]);

  const reset = () => {
    setJobId(undefined);
    startMutation.reset();
  };

  return {
    start: (params?: ILicenseRecalculateParams) => startMutation.mutate(params),
    isStarting: startMutation.isLoading,
    isJobLoading: Boolean(jobId) && statusQuery.isLoading,
    job,
    reset,
  };
};
